import { makeAutoObservable, runInAction } from "mobx";
import { IMerchantOnboarding, IMerchantOnboardingResolution } from "../onboarding-types";
import { IOnboardingResolution } from "../components/choose-resolution-screen/choose-resolution-screen";
import { RESOLUTION_ID, RESOLUTION_TYPE } from "../../resolutions/constants";
import OnboardingDataStore, {
    IGetMerchantOnboardingModel,
    IOnboardingScreenStore
} from "./onboarding-data-store";
import { ONBOARDING_STEPS } from "../constants";
import { OnboardingNavigationStore } from "./onboarding-navigation-store";
import { FEATURES } from "../../../models/Plan";
import onboardingPlansStores from "./onboarding-plans-stores";
import { isSet } from "../../../models/model-types-methods";
import currentPortalStore from "../../../stores/current-portal-store";
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../models/ProfileCapabilities";

/** Maps Resolution id to feature_id */
const MAP_RESOLUTION_TO_FEATURE: Partial<{ [key in RESOLUTION_ID]: FEATURES }> = {
    [RESOLUTION_ID.PRODUCT_EXCHANGE]: FEATURES.PRODUCT_EXCHANGE,
}

/** Maps onboarding refund resolution ids to REFUND_RESOLUTION supportedRefundTypes */
const MAP_RESOLUTION_ID_TO_REFUND_TYPE: Partial<{ [key in RESOLUTION_ID]: RESOLUTION_TYPE }> = {
    [RESOLUTION_ID.REFUND_TO_CREDIT]: RESOLUTION_TYPE.Credits,
    [RESOLUTION_ID.ORIG_PAY_REFUND]: RESOLUTION_TYPE.Refund,
    [RESOLUTION_ID.REFUND_TO_GIFT_CARD]: RESOLUTION_TYPE.GiftCard,
    [RESOLUTION_ID.REFUND_TO_BANK]: RESOLUTION_TYPE.BankRefund,
}

class ChooseResolutionScreenStore implements IOnboardingScreenStore  {
    private _resolutionsMap: Map<RESOLUTION_ID, boolean> = new Map();
    private _getModel: IGetMerchantOnboardingModel;
    private _onboardingDataStore: OnboardingDataStore;
    private readonly _stepName: ONBOARDING_STEPS = ONBOARDING_STEPS.CHOOSE_RETURN_TYPE;
    private _isLoading: boolean = false;

    constructor(onboardingDataStore: OnboardingDataStore, private _navigationStore: OnboardingNavigationStore) {
        this._onboardingDataStore = onboardingDataStore;
        this._getModel = this._onboardingDataStore.getModel;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get isLoading(): boolean { return this._isLoading; }

    public hasChanges(): boolean {
        const modelResolutions = new Set(
            (this._getModel()?.merchant_onboarding_resolutions ?? []).map(res => res.resolution_id)
        );

        const storedResolutions = new Set(this._resolutionsMap.keys());

        if (modelResolutions.size !== storedResolutions.size) {
            return true;
        }

        for (const id of modelResolutions) {
            if (!storedResolutions.has(id)) {
                return true;
            }
        }

        return false;
    }

    public discardChanges(): void {
        this._resolutionsMap = new Map(
            (this._getModel()?.merchant_onboarding_resolutions ?? []).map(resolution => [resolution.resolution_id, true])
        );
    }

    public clearAllSelections(): void {
        this._resolutionsMap = new Map();
    }

    private get getSelectedResolutions(): IMerchantOnboardingResolution[] {
        return Array.from(this._resolutionsMap.keys()).map(id => ({ resolution_id: id }));
    }

    public getUpdatedModel(): IMerchantOnboarding {
        return { 
            ...this._getModel(), 
            merchant_onboarding_resolutions: this.getSelectedResolutions
        } as IMerchantOnboarding;
    }

    public getMixPanelData(): Record<string, any> {
        return {
            merchant_onboarding_resolutions: this.getSelectedResolutions.map((merchantOnboardingResolution) =>
                Object.keys(RESOLUTION_ID).find(key => RESOLUTION_ID[key as keyof typeof RESOLUTION_ID] === merchantOnboardingResolution.resolution_id)
            )
        }
    }

    public isComplete(): boolean {
        return this._resolutionsMap.size > 0;
    }

    public isResolutionSelected(resolution: IOnboardingResolution): boolean {
        return this._resolutionsMap.has(resolution.id);
    }

    public toggleResolution(resolution: IOnboardingResolution): void {
        if (this._resolutionsMap.has(resolution.id)) {
            this._resolutionsMap.delete(resolution.id);
        } else {
            this._resolutionsMap.set(resolution.id, true);
        }
    }

       public isPlatformSupportedResolution(resolutionId: RESOLUTION_ID): boolean {
        return !currentPortalStore.platform || currentPortalStore.platform.isResolutionSupportedById(resolutionId);
    }

    public isResolutionEnabled(resolutionId: RESOLUTION_ID): boolean {
        // First check if the resolution is supported by the platform
        if (!this.isPlatformSupportedResolution(resolutionId)) {
            return false;
        }

        const refundType = MAP_RESOLUTION_ID_TO_REFUND_TYPE[resolutionId];
        if (isSet(refundType)) {
            const refundResolutionCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.REFUND_RESOLUTION);
            if (!((refundResolutionCapability.supportedRefundTypes ?? []) as readonly RESOLUTION_TYPE[]).includes(refundType!)) {
                return false;
            }
        }

        const featureId = MAP_RESOLUTION_TO_FEATURE[resolutionId];
        /** not all resolutions has correlated features */
        if (!isSet(featureId)) {
            return true;
        }

        const selectedPlanId = this._getModel()?.plan_id; /** at this point we expect the plan id to be set */
        if (!isSet(selectedPlanId)) {
            console.error('[onboarding wizard] should never happen, missing selected plan id');
            return true;
        }

        const plan = onboardingPlansStores.getPlanById(selectedPlanId!);
        return plan!.isFeatureIncluded(featureId!);
    }

    public filterResolutions(resolutions: IOnboardingResolution[]): IOnboardingResolution[] {
        return resolutions.filter(resolution => this.isPlatformSupportedResolution(resolution.id));
    }

    public previousStep(): void {
        this._navigationStore.previousStep();
    }

    public async nextStep(): Promise<void> {
        this._isLoading = true;
        await this._onboardingDataStore.save(this._stepName);
        runInAction(() => {
            this._navigationStore.nextStep();
            this._isLoading = false;
        })
    }
}

export default ChooseResolutionScreenStore;
